import { useTheme } from '../../context/ThemeContext';
import { useFont } from '../../context/FontContext';
import { useSound } from '../../context/SoundContext';
import { useLanguage } from '../../context/LanguageContext';
import { useDateTime, LocaleType } from '../../context/DateTimeContext';
import DynamicForm from '../../components/DynamicForm/DynamicForm';
import { Field as FieldConfig, SelectField } from '../../types/types.form';
import styles from './SettingsPage.module.css';
import { IconPack, useIconPack } from '../../context/IconPackContext';
import { FontFamilyType, FontSizeType } from '../../config/fonts';
import { getLocaleDisplayLabel, LOCALES, TIMEZONES } from '../../config/timezones';
import { ToastService } from '../../service/toast.service';
import ColorPalette from '../../components/ColorPalette/ColorPalette';

interface SettingsFormValues {
  fontSize: 'small' | 'medium' | 'large' | 'extra-large';
  fontFamily: 'montserrat' | 'system' | 'sans-serif' | 'serif' | 'monospace'; 
  iconPack: IconPack;
  soundEnabled: boolean;
  soundVolume: number;
  dateLocale: LocaleType;
  timezone: string;
  [key: string]: string | number | boolean;
}

export default function SettingsPage() {
  const { customColor } = useTheme();
  const { fontSize, setFontSize, fontFamily, setFontFamily } = useFont();
  const { soundEnabled, setSoundEnabled, soundVolume, setSoundVolume } = useSound();
  const { language, setLanguage, t } = useLanguage();
  const { setIconPack } = useIconPack();
  const { locale, setLocale, timezone, setTimezone } = useDateTime();

  const settingsConfig: FieldConfig[] = [
    {
      name: "dateLocale",
      type: "select",
      label: "dateFormat",
      options: LOCALES.map(l => l.value),
      customOptions: LOCALES.map(l => ({
        value: l.value,
        label: getLocaleDisplayLabel(l)
      })),
      defaultValue: locale,
    } as SelectField & { customOptions?: { value: string; label: string }[] },
    {
      name: "timezone",
      type: "select",
      label: "timezone",
      options: TIMEZONES.map(tz => tz.value),
      customOptions: TIMEZONES.map(tz => ({
        value: tz.value,
        label: tz.label
      })),
      defaultValue: timezone,
    } as SelectField & { customOptions?: { value: string; label: string }[] },
    {
      name: "fontSize",
      type: "select",
      customType: "fontSizeSelector",
      label: "Font Size",
      options: ["small", "medium", "large", "extra-large"],
      defaultValue: fontSize,
    },
    {
      name: "fontFamily",
      type: "select",
      customType: "fontFamilySelector",
      label: "Font Family",
      options: ["montserrat", "system", "sans-serif", "serif", "monospace"],
      defaultValue: fontFamily,
    },
    {
      name: "iconPack",
      type: "select",
      customType: "iconPackSelector",
      label: "Icon Style",
      options: ["solid", "regular"],
      defaultValue: useIconPack().iconPack,
    },
    {
      name: "testIcon",
      type: "select",
      customType: "iconPickSelector",
      label: "Choose Icon",
      options: [],
      defaultValue: "",
    },
    {
      name: 'soundEnabled',
      type: 'checkbox',
      label: 'enableSoundNotifications', 
      defaultValue: soundEnabled,
    },
    {
      name: 'soundVolume',
      type: 'range',
      label: 'soundVolume', 
      min: 0,
      max: 100,
      step: 5,
      defaultValue: soundVolume,
    },
  ];

  const handleSubmit = (values: SettingsFormValues) => {
    try {
      setFontSize(values.fontSize as FontSizeType);
      setFontFamily(values.fontFamily as FontFamilyType);
      setIconPack(values.iconPack as IconPack);
      setSoundEnabled(values.soundEnabled);
      setSoundVolume(values.soundVolume);
      setLocale(values.dateLocale as LocaleType);
      setTimezone(values.timezone);

      ToastService.success(t("settingsSaved"));
    } catch {
      ToastService.error(t("settingsFailed"));
    }
  };

  return (
    <div className={styles.settingsPage}>
      <ColorPalette />

      <DynamicForm<SettingsFormValues> config={settingsConfig} onSubmit={handleSubmit} />
    </div>
  );
}